import { Link } from '@tanstack/react-router';
import { Calendar, Megaphone, User } from 'lucide-react';
import { type ReactNode } from 'react';

import {
    ReferencePillContent,
    WRAPPING_REFERENCE_PILL_CLASS,
} from '@apps/web/components/ui/reference_pill';
import {
    formatDateAndTimeWithWeekday,
    formatTime,
    getDateStringInTimezone,
} from '@apps/web/lib/date_utils';
import { ENTITY_TINTS } from '@apps/web/lib/entity_palette';
import { chipClassForBucket } from '@apps/web/lib/shift_reference_chip_style';
import { cn } from '@apps/web/lib/utils';
import { type Shift } from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

type WarningEntity = {
    readonly id: string;
    readonly label: string;
    readonly type: 'shift' | 'caregiver' | 'outreach';
    readonly date?: string;
};

export function OutreachWarningPill({
    entity,
}: {
    readonly entity: WarningEntity;
}) {
    const className = cn(
        WRAPPING_REFERENCE_PILL_CLASS,
        entity.type === 'shift'
            ? chipClassForBucket('scheduled')
            : ENTITY_TINTS[entity.type === 'outreach' ? 'callout' : 'caregiver']
                  .pill,
    );
    const content = (
        <ReferencePillContent
            Icon={
                entity.type === 'shift'
                    ? Calendar
                    : entity.type === 'caregiver'
                      ? User
                      : Megaphone
            }
            label={entity.label}
            wrap={true}
        />
    );
    switch (entity.type) {
        case 'shift':
            return (
                <Link
                    to="/shifts"
                    search={{ shiftId: entity.id, date: entity.date }}
                    className={className}
                >
                    {content}
                </Link>
            );
        case 'caregiver':
            return (
                <Link
                    to="/caregivers"
                    search={{ caregiverId: entity.id }}
                    className={className}
                >
                    {content}
                </Link>
            );
        case 'outreach':
            return (
                <Link
                    to="/outreaches/$callout-id"
                    params={{ 'callout-id': entity.id }}
                    className={className}
                >
                    {content}
                </Link>
            );
        default:
            return assert.never(entity.type);
    }
}

export type OutreachWarningShift = Pick<
    Shift,
    'id' | 'patient_name' | 'shift_start_time' | 'shift_end_time'
> & {
    readonly active_fill_outreach_id?: string | null;
};

/** Render API warning IDs from the host's loaded entities, without fetching or
 * guessing the type of an unknown ID. Missing records keep a readable label. */
export function formatOutreachWarningMessage(
    message: string,
    {
        shifts = [],
        caregivers = [],
        outreachIds = [],
        timezone,
    }: {
        readonly shifts?: ReadonlyArray<OutreachWarningShift>;
        readonly caregivers?: ReadonlyArray<{
            readonly id: string;
            readonly name: string;
        }>;
        readonly outreachIds?: ReadonlyArray<string>;
        readonly timezone: string;
    },
): ReactNode {
    // Also consume Python's UUID('…') wrapper used in list-valued API errors.
    const parts = message.split(
        /(?:UUID\(['"])?\b([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\b(?:['"]\))?/i,
    );
    if (parts.length === 1) {
        return message;
    }
    const referencedIds = new Set(
        parts
            .filter((_, index) => index % 2 === 1)
            .map((id) => id.toLowerCase()),
    );
    const entities = new Map<string, WarningEntity>();
    for (const id of outreachIds) {
        entities.set(id.toLowerCase(), {
            id,
            type: 'outreach',
            label: 'Outreach',
        });
    }
    for (const shift of shifts) {
        if (referencedIds.has(shift.id.toLowerCase())) {
            entities.set(shift.id.toLowerCase(), {
                id: shift.id,
                type: 'shift',
                label: `${shift.patient_name} · ${formatDateAndTimeWithWeekday(shift.shift_start_time, timezone)}–${formatTime(shift.shift_end_time, timezone)}`,
                date: getDateStringInTimezone(
                    new Date(shift.shift_start_time),
                    timezone,
                ),
            });
        }
        if (shift.active_fill_outreach_id) {
            entities.set(shift.active_fill_outreach_id.toLowerCase(), {
                id: shift.active_fill_outreach_id,
                type: 'outreach',
                label: 'Active outreach',
            });
        }
    }
    for (const caregiver of caregivers) {
        entities.set(caregiver.id.toLowerCase(), {
            ...caregiver,
            type: 'caregiver',
            label: caregiver.name,
        });
    }
    return parts.map((part, index) => {
        if (index % 2 === 0) {
            return part;
        }
        const entity = entities.get(part.toLowerCase());
        return entity ? (
            <OutreachWarningPill key={index} entity={entity} />
        ) : (
            'unavailable record'
        );
    });
}
